import {
  MAX_TITLE_LENGTH,
  MAX_BODY_LENGTH,
  MIN_TITLE_LENGTH,
  MIN_BODY_LENGTH,
} from '../utils/index.js';

class NoteForm extends HTMLElement {
  _boundHandlers = {};

  constructor() {
    super();
  }

  connectedCallback() {
    this.render();
    this._attachEventListeners();
  }

  disconnectedCallback() {
    this._removeEventListeners();
  }

  _removeEventListeners() {
    const form = this.querySelector('form');
    const titleInput = this.querySelector('#noteTitle');
    const bodyInput = this.querySelector('#noteBody');
    const closeBtn = this.querySelector('.note-form-close-btn');
    const cancelBtn = this.querySelector('.note-form-btn-secondary');

    if (closeBtn && this._boundHandlers.closeClick) {
      closeBtn.removeEventListener('click', this._boundHandlers.closeClick);
    }
    if (cancelBtn && this._boundHandlers.cancelClick) {
      cancelBtn.removeEventListener('click', this._boundHandlers.cancelClick);
    }
    if (titleInput && this._boundHandlers.titleInput) {
      titleInput.removeEventListener('input', this._boundHandlers.titleInput);
    }
    if (bodyInput && this._boundHandlers.bodyInput) {
      bodyInput.removeEventListener('input', this._boundHandlers.bodyInput);
    }
    if (form && this._boundHandlers.formSubmit) {
      form.removeEventListener('submit', this._boundHandlers.formSubmit);
    }
  }

  _attachEventListeners() {
    const form = this.querySelector('form');
    const titleInput = this.querySelector('#noteTitle');
    const bodyInput = this.querySelector('#noteBody');
    const closeBtn = this.querySelector('.note-form-close-btn');
    const cancelBtn = this.querySelector('.note-form-btn-secondary');

    // Close handlers
    this._boundHandlers.closeClick = () => this._closeModal();
    this._boundHandlers.cancelClick = () => this._closeModal();

    closeBtn?.addEventListener('click', this._boundHandlers.closeClick);
    cancelBtn?.addEventListener('click', this._boundHandlers.cancelClick);

    // Real-time validation for title
    this._boundHandlers.titleInput = () => {
      const value = titleInput.value;
      const charCount = this.querySelector('#titleCharCount');
      const validation = this.querySelector('#titleValidation');

      charCount.textContent = `${value.length}/${MAX_TITLE_LENGTH}`;

      if (value.length === 0) {
        titleInput.classList.remove('valid', 'invalid');
        validation.textContent = '';
      } else if (value.length < MIN_TITLE_LENGTH) {
        titleInput.classList.remove('valid');
        titleInput.classList.add('invalid');
        validation.textContent = `Title must be at least ${MIN_TITLE_LENGTH} characters`;
      } else if (value.length > MAX_TITLE_LENGTH) {
        titleInput.classList.remove('valid');
        titleInput.classList.add('invalid');
        validation.textContent = `Title is too long`;
      } else {
        titleInput.classList.remove('invalid');
        titleInput.classList.add('valid');
        validation.textContent = '';
      }

      this._updateSubmitButton();
    };

    titleInput?.addEventListener('input', this._boundHandlers.titleInput);

    // Real-time validation for body
    this._boundHandlers.bodyInput = () => {
      const value = bodyInput.value;
      const charCount = this.querySelector('#bodyCharCount');
      const validation = this.querySelector('#bodyValidation');

      charCount.textContent = `${value.length}/${MAX_BODY_LENGTH}`;

      if (value.length === 0) {
        bodyInput.classList.remove('valid', 'invalid');
        validation.textContent = '';
      } else if (value.length < MIN_BODY_LENGTH) {
        bodyInput.classList.remove('valid');
        bodyInput.classList.add('invalid');
        validation.textContent = `Content must be at least ${MIN_BODY_LENGTH} characters`;
      } else if (value.length > MAX_BODY_LENGTH) {
        bodyInput.classList.remove('valid');
        bodyInput.classList.add('invalid');
        validation.textContent = `Content is too long`;
      } else {
        bodyInput.classList.remove('invalid');
        bodyInput.classList.add('valid');
        validation.textContent = '';
      }

      this._updateSubmitButton();
    };

    bodyInput?.addEventListener('input', this._boundHandlers.bodyInput);

    // Form submission
    this._boundHandlers.formSubmit = (event) => {
      event.preventDefault();

      const title = titleInput.value.trim();
      const body = bodyInput.value.trim();

      if (this._isValid(title, body)) {
        const noteData = {
          title: title,
          body: body,
        };

        this.dispatchEvent(
          new CustomEvent('note-added', {
            detail: { note: noteData },
            bubbles: true,
          })
        );

        this._resetForm();
        this._closeModal();
      }
    };

    form?.addEventListener('submit', this._boundHandlers.formSubmit);
  }

  _isValid(title, body) {
    return (
      title.length >= MIN_TITLE_LENGTH &&
      title.length <= MAX_TITLE_LENGTH &&
      body.length >= MIN_BODY_LENGTH &&
      body.length <= MAX_BODY_LENGTH
    );
  }

  _updateSubmitButton() {
    const titleInput = this.querySelector('#noteTitle');
    const bodyInput = this.querySelector('#noteBody');
    const submitBtn = this.querySelector('button[type="submit"]');

    if (titleInput && bodyInput && submitBtn) {
      submitBtn.disabled = !this._isValid(titleInput.value.trim(), bodyInput.value.trim());
    }
  }

  _resetForm() {
    const form = this.querySelector('form');
    const titleInput = this.querySelector('#noteTitle');
    const bodyInput = this.querySelector('#noteBody');
    const titleCharCount = this.querySelector('#titleCharCount');
    const bodyCharCount = this.querySelector('#bodyCharCount');

    form?.reset();
    titleInput?.classList.remove('valid', 'invalid');
    bodyInput?.classList.remove('valid', 'invalid');
    if (titleCharCount) titleCharCount.textContent = `0/${MAX_TITLE_LENGTH}`;
    if (bodyCharCount) bodyCharCount.textContent = `0/${MAX_BODY_LENGTH}`;
  }

  _closeModal() {
    this.dispatchEvent(
      new CustomEvent('form-closed', {
        bubbles: true,
      })
    );
  }

  render() {
    this._removeEventListeners();

    this.innerHTML = `
      <div class="note-form-container">
        <div class="note-form-header">
          <h2 class="note-form-title" id="formTitle">New Note</h2>
          <button class="note-form-close-btn" type="button" aria-label="Close form">
            <span class="material-symbols-outlined" aria-hidden="true">close</span>
          </button>
        </div>
        <form aria-labelledby="formTitle">
          <div class="note-form-group">
            <label for="noteTitle">Title</label>
            <input 
              type="text" 
              id="noteTitle" 
              name="title" 
              class="note-form-input"
              placeholder="Enter note title..."
              maxlength="${MAX_TITLE_LENGTH}"
              required
              aria-describedby="titleValidation titleCharCount"
            />
            <span id="titleValidation" class="note-form-validation" role="alert"></span>
            <span id="titleCharCount" class="note-form-char-count" aria-live="polite">0/${MAX_TITLE_LENGTH}</span>
          </div>
          
          <div class="note-form-group">
            <label for="noteBody">Content</label>
            <textarea 
              id="noteBody" 
              name="body" 
              class="note-form-textarea"
              placeholder="Write your note here..."
              maxlength="${MAX_BODY_LENGTH}"
              required
              aria-describedby="bodyValidation bodyCharCount"
            ></textarea>
            <span id="bodyValidation" class="note-form-validation" role="alert"></span>
            <span id="bodyCharCount" class="note-form-char-count" aria-live="polite">0/${MAX_BODY_LENGTH}</span>
          </div>
          
          <div class="note-form-actions">
            <button type="button" class="note-form-btn note-form-btn-secondary">Cancel</button>
            <button type="submit" class="note-form-btn note-form-btn-primary" disabled>Create Note</button>
          </div>
        </form>
      </div>
    `;
  }
}

customElements.define('note-form', NoteForm);
